import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, Send, ShieldCheck, Users } from "lucide-react";
import axios from "axios";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "confirmph_sales_agent_draft_v1";

const SALES_EXPERIENCE_OPTIONS = [
  "Field sales", "Door-to-door sales", "Business-to-business sales", "Retail sales",
  "Phone sales", "Cold calling", "Appointment setting", "Lead generation",
  "Account management", "Customer service", "Product demonstrations",
  "Software / SaaS sales", "Automotive industry", "Vehicle rental industry",
  "Tourism / hospitality", "Real estate", "Insurance", "Telecommunications",
  "Banking / finance", "Medical sales", "FMCG sales", "Merchant acquisition",
  "POS / payment systems", "Digital services", "Facebook / Messenger selling",
  "Commission-based sales", "No professional sales experience yet",
];

const SKILL_LIST = [
  "Speaking to strangers", "Walking into a business without an appointment",
  "Speaking with owners/managers", "Starting conversations", "Asking discovery questions",
  "Listening to customer problems", "Explaining products simply", "Product demonstrations",
  "Handling objections", "Handling rejection", "Following up", "Closing sales",
  "Negotiation", "Building customer relationships", "Using CRM systems",
  "Using web applications", "Using smartphones for work", "Using Google Maps",
  "Planning efficient travel routes", "Writing accurate visit notes",
  "Working independently", "Managing your own daily schedule",
];

const LANG_LIST = ["English", "Cebuano / Bisaya", "Tagalog / Filipino", "Hiligaynon / Ilonggo"];

const FIELD_CAPS = [
  "Willing to travel locally", "Willing to travel between municipalities",
  "Willing to travel between provinces", "Comfortable working outside an office",
  "Comfortable entering businesses alone",
  "Comfortable approaching owners/managers without an appointment",
  "Comfortable visiting multiple businesses in one day",
  "Comfortable using public transport for field work",
  "Has own motorcycle", "Has own car", "Has valid driver's license",
  "Can use Google Maps confidently", "Can independently plan a field route",
];

const EQUIPMENT = [
  "Smartphone", "Reliable mobile data", "Phone camera", "Power bank", "Laptop",
  "Tablet", "Headset", "Own motorcycle", "Own car", "Printer access",
  "Can scan documents using phone", "Comfortable installing work apps",
  "Comfortable using QR codes", "Comfortable demonstrating a web app",
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WORK_PREF = ["Full-time", "Part-time", "Field days only", "Flexible schedule",
                     "Commission/project-based", "Open to different arrangements"];

const SCENARIOS = [
  { key: "s1", q: `Owner says: "We already use Messenger. We don't need another system.": What would you say?` },
  { key: "s2", q: `Owner is busy and says: "I don't have time.": What would you do?` },
  { key: "s3", q: `Business creates a free account but one week later has not created any booking.: What would you do?` },
  { key: "s4", q: `Owner says: "Why should I pay ₱1,200 every month when Messenger is free?": How would you respond?` },
  { key: "s5", q: `Owner asks about a ConfirmPH feature and you don't know the answer.: What would you do?` },
  { key: "s6", q: `You visit 15 businesses. 12 reject you. 2 say maybe. 1 creates an account.: How would you evaluate the day and what would you do next?` },
  { key: "s7", q: `Owner says: "I'll join if you tell customers we're officially guaranteed by ConfirmPH." ConfirmPH does not provide this guarantee.: What would you say?` },
  { key: "s8", q: `Another salesperson suggests creating fake business accounts to increase signup numbers.: What would you do?` },
];

const ETHICS_CLAUSES = [
  "I will not lie about ConfirmPH features.",
  "I will not promise guaranteed business results.",
  "I will not register businesses without permission.",
  "I will not create fake leads or accounts.",
  "I will not manipulate performance statistics.",
  "I will not collect money unless specifically authorized.",
  "I will not pressure or harass businesses.",
  "I will respect a clear request not to be contacted again.",
  "I will accurately record business feedback.",
  "I will report mistakes honestly.",
  "I will protect confidential business information.",
  "I will not tell a business that ConfirmPH makes it government-approved or automatically legally compliant.",
  "I will not say ConfirmPH provides BIR-approved or BIR-registered books.",
  "I will not say ConfirmPH guarantees that a business's vehicles or drivers are legal or safe.",
  "I will not say ConfirmPH holds customers' security deposits or provides insurance.",
  "I understand that dishonest sales activity may result in immediate removal.",
];

const MOTIVATION_QS = [
  ["why_confirmph",     "Why do you want to work with ConfirmPH?"],
  ["why_sales",         "Why are you interested in sales?"],
  ["why_good",          "What makes you good at speaking to business owners?"],
  ["hardest_part",      "What is the hardest part of sales for you?"],
  ["what_motivates",    "What motivates you to keep working after rejection?"],
  ["would_leave",       "What would make you leave a sales job?"],
  ["good_manager",      "What type of manager helps you perform best?"],
  ["improve",           "What do you want to improve professionally?"],
];

const emptyDraft = () => ({
  full_legal_name: "", preferred_name: "", date_of_birth: "", gender: "",
  nationality: "", civil_status: "", address: "", city: "", province: "",
  country: "Philippines",
  mobile: "", email: "", messenger: "", whatsapp: "", viber: "", telegram: "",
  preferred_contact: "", emergency_contact_name: "", emergency_contact_number: "",
  emergency_relationship: "",
  gov_id_type: "", gov_id_number: "", tin: "", sss: "", philhealth: "", pagibig: "",
  driver_license: "",
  education_level: "", school: "", course: "", year_completed: "",
  years_total_work: "", years_sales: "", years_field_sales: "",
  previous_titles: "", previous_employers: "", work_description: "",
  sales_experience_types: [],
  what_sold: "", customers: "", b2b_or_b2c: "", sales_channel: "",
  prospects_per_week: "", typical_closes: "", avg_monthly_sales: "",
  largest_close: "", own_leads: "", closed_yourself: "", had_targets: "",
  met_targets: "", why_left: "",
  skills: {}, languages: {}, other_languages: "",
  current_location: "", primary_area: "", other_areas: "",
  field_capabilities: [],
  primary_transport: "", owns_transport: "", vehicle_registered: "", daily_coverage: "",
  equipment: [],
  work_preference: [], available_days: [], available_hours: "",
  earliest_start: "", expected_monthly_salary: "", expected_daily_rate: "",
  expected_commission: "", ok_with_bonuses: "", ok_with_targets: "",
  scenario_answers: {},
  pitch_30s: "", one_sentence: "", discovery_q1: "", discovery_q2: "", discovery_q3: "",
  rejection_comfort: null, after_rejection: "", daily_approach_comfort: "",
  rejection_impact: "",
  ethics: {}, motivation: {}, references: [ref(), ref()],
  resume_url: "", linkedin_url: "", portfolio_url: "",
  accept_privacy: false, accept_accuracy: false,
});

function ref() { return { name: "", company: "", position: "", relationship: "", phone: "", email: "" }; }

/* ---------- Small UI primitives (kept local; no new files) ------------ */

function L({ children, required }) {
  return <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
    {children}{required && <span className="text-red-600"> *</span>}
  </label>;
}
function I(props) {
  return <input {...props}
    className={`w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${props.className||""}`} />;
}
function T({ rows = 3, ...props }) {
  return <textarea rows={rows} {...props}
    className={`w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${props.className||""}`} />;
}
function Grid({ children, cols = 2 }) {
  return <div className={`grid gap-3 ${cols === 2 ? "sm:grid-cols-2" : cols === 3 ? "sm:grid-cols-3" : ""}`}>{children}</div>;
}
function CheckboxList({ options, value, onChange, testPrefix }) {
  const has = (o) => value.includes(o);
  const toggle = (o) => onChange(has(o) ? value.filter(x => x !== o) : [...value, o]);
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {options.map(o => (
        <label key={o} className="inline-flex items-center gap-2 text-sm text-slate-700 py-1"
                data-testid={testPrefix ? `${testPrefix}-${o.slice(0,20).replace(/\s+/g,"_")}` : undefined}>
          <input type="checkbox" checked={has(o)} onChange={() => toggle(o)}
                  className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          <span>{o}</span>
        </label>
      ))}
    </div>
  );
}
function Rating({ value, onChange, testId }) {
  return (
    <div className="inline-flex gap-1" role="radiogroup" data-testid={testId}>
      {[1,2,3,4,5].map(n => (
        <button type="button" key={n} onClick={() => onChange(n)}
                 className={`w-8 h-8 rounded-full text-sm font-medium border ${
                   value === n ? "bg-[#1B4332] text-white border-[#1B4332]"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
          {n}
        </button>
      ))}
    </div>
  );
}

/* --------------------------- Page ----------------------------------- */

export default function SalesAgentCareers() {
  const [d, setD] = useState(() => {
    try { const s = localStorage.getItem(DRAFT_KEY); if (s) return { ...emptyDraft(), ...JSON.parse(s) }; }
    catch(_e) { /* ignore */ }
    return emptyDraft();
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {reference, id}
  const set = (patch) => setD(prev => ({ ...prev, ...patch }));

  // Autosave draft
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch(_e) { /* ignore */ }
  }, [d]);

  const setSkill = (k, v) => set({ skills: { ...d.skills, [k]: v } });
  const setLang  = (k, v) => set({ languages: { ...d.languages, [k]: v } });
  const setScen  = (k, v) => set({ scenario_answers: { ...d.scenario_answers, [k]: v } });
  const setEth   = (k, v) => set({ ethics: { ...d.ethics, [k]: v } });
  const setMot   = (k, v) => set({ motivation: { ...d.motivation, [k]: v } });
  const setRef   = (i, patch) => {
    const refs = [...(d.references || [ref(), ref()])];
    refs[i] = { ...refs[i], ...patch };
    set({ references: refs });
  };

  const allEthicsChecked = ETHICS_CLAUSES.every((_, i) => d.ethics[`e${i}`] === true);
  const requiredOK =
    d.full_legal_name.trim() && d.mobile.trim() && /.+@.+\..+/.test(d.email) &&
    d.accept_accuracy && d.accept_privacy && allEthicsChecked;

  const submit = async () => {
    if (!requiredOK) {
      toast.error("Please fill Full name, Mobile, Email, all ethics declarations, and accept both consents.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/recruitment/applications`, d);
      setSubmitted(data);
      try { localStorage.removeItem(DRAFT_KEY); } catch(_e) { /* ignore */ }
      toast.success("Application submitted!");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please review the form.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SubmittedScreen submitted={submitted} />;

  return (
    <div data-testid="sales-agent-page">
      <SEO title="Become a ConfirmPH Sales Agent: Careers"
           description="Apply to join ConfirmPH as a field sales, business onboarding, or local market representative."
           canonical="/careers/sales-agent" />

      {/* Hero */}
      <section className="bg-[#F8FAF9] border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Careers · Join ConfirmPH</div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
            Become a ConfirmPH Sales Agent
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Apply to join ConfirmPH as a field sales representative, business onboarding
            representative, local market representative, follow-up specialist, or future
            sales team leader.
          </p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            ConfirmPH helps Philippine rental businesses turn scattered Messenger messages,
            phone calls, Facebook inquiries, notebooks, screenshots, and informal agreements
            into clear booking records, customer confirmations, warnings, reminders, and a
            proper booking paper trail. Our sales representatives bring ConfirmPH directly
            to real businesses.
          </p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            <strong>Registration alone is not the goal.</strong> Real business activation matters.
          </p>
        </div>
      </section>

      {/* Role information */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <Accordion type="multiple" defaultValue={["about"]}>
            <AccordionItem value="about">
              <AccordionTrigger data-testid="acc-about" className="text-lg font-semibold">About the Role</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed space-y-3">
                <p>This is not a job where you sit behind a desk. Depending on your role, you may visit rental businesses in person, speak directly with owners and managers, explain and demonstrate ConfirmPH, help businesses create their account and add their first vehicle or booking, follow up, and record feedback and objections.</p>
                <p>You do not need to pressure people. You do need to be confident enough to walk through a business door, introduce yourself professionally, and start a conversation.</p>
                <p><strong>Conversation philosophy:</strong> Ask how the business currently handles bookings. Understand the problem. Demonstrate the relevant ConfirmPH solution. Do not pressure the owner.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="roles">
              <AccordionTrigger data-testid="acc-roles" className="text-lg font-semibold">Possible Roles</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed space-y-4">
                {[
                  ["Field Sales Representative", "Physically visit businesses and introduce ConfirmPH to owners, managers, and decision-makers."],
                  ["Business Onboarding Representative", "Help interested businesses actually start using ConfirmPH: account setup, adding vehicles, first booking, customer confirmation links."],
                  ["Sales & Onboarding Representative", "Combined role covering: find business → speak with owner → demonstrate → register → onboard → follow up → activate."],
                  ["Follow-Up / Account Activation Representative", "Follow up with businesses that were interested, registered but didn't finish, or need help activating."],
                  ["Local Market Representative", "Represent ConfirmPH in a specific city, municipality, province, or territory."],
                  ["Future Team Leader / Area Sales Supervisor", "Potential promotion for strong performers: help train and supervise additional field reps."],
                ].map(([t,b]) => (
                  <div key={t}><div className="font-semibold text-slate-900">{t}</div><div>{b}</div></div>
                ))}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="looking">
              <AccordionTrigger data-testid="acc-looking" className="text-lg font-semibold">What We Are Looking For</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                Comfortable speaking to strangers · comfortable walking into businesses · friendly but professional · confident without being arrogant · persistent without becoming annoying · handles rejection calmly · honest · reliable · organized · comfortable using smartphones · able to explain things simply · good listener · asks discovery questions · comfortable with owners/managers · willing to learn · works independently · follows up.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="not">
              <AccordionTrigger data-testid="acc-not" className="text-lg font-semibold">What ConfirmPH Does NOT Want</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                Representatives may not lie, exaggerate features, promise guaranteed results, pretend unavailable features exist, create fake accounts, register businesses without permission, create fake activity, manipulate statistics, harass prospects, collect unauthorized payments, promise unauthorized discounts, or misrepresent ConfirmPH.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="how">
              <AccordionTrigger data-testid="acc-how" className="text-lg font-semibold">How Sales Should Work</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                <p className="italic">"How do you currently keep track of your bookings?"</p>
                <p className="mt-2">The owner may say: Messenger, Facebook, notebook, spreadsheet, calendar, phone, staff messages, memory, another booking system.</p>
                <p className="mt-2">Then demonstrate the relevant ConfirmPH workflow. A good sales conversation should feel like a useful business conversation, not a scripted attack.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Application form */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Application</h2>
          <p className="mt-2 text-sm text-slate-600">Every section is expandable. Your progress is saved automatically to this device.</p>

          <Accordion type="multiple" defaultValue={["p01"]} className="mt-6">
            {/* 01 Personal */}
            <AccordionItem value="p01">
              <AccordionTrigger data-testid="sec-01" className="font-semibold">01 · Personal Information</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <div><L required>Full legal name</L>
                  <I value={d.full_legal_name} onChange={e => set({ full_legal_name: e.target.value })} data-testid="in-full-name" /></div>
                <Grid>
                  <div><L>Preferred name</L><I value={d.preferred_name} onChange={e => set({ preferred_name: e.target.value })} data-testid="in-preferred" /></div>
                  <div><L>Date of birth</L><I type="date" value={d.date_of_birth} onChange={e => set({ date_of_birth: e.target.value })} /></div>
                  <div><L>Sex / gender (optional)</L><I value={d.gender} onChange={e => set({ gender: e.target.value })} /></div>
                  <div><L>Nationality</L><I value={d.nationality} onChange={e => set({ nationality: e.target.value })} /></div>
                  <div><L>Civil status (optional)</L><I value={d.civil_status} onChange={e => set({ civil_status: e.target.value })} /></div>
                </Grid>
                <div><L>Current address</L><I value={d.address} onChange={e => set({ address: e.target.value })} /></div>
                <Grid cols={3}>
                  <div><L>City / municipality</L><I value={d.city} onChange={e => set({ city: e.target.value })} data-testid="in-city" /></div>
                  <div><L>Province</L><I value={d.province} onChange={e => set({ province: e.target.value })} data-testid="in-province" /></div>
                  <div><L>Country</L><I value={d.country} onChange={e => set({ country: e.target.value })} /></div>
                </Grid>
              </AccordionContent>
            </AccordionItem>

            {/* 02 Contact */}
            <AccordionItem value="p02">
              <AccordionTrigger data-testid="sec-02" className="font-semibold">02 · Contact Information</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <Grid>
                  <div><L required>Mobile number</L><I value={d.mobile} onChange={e => set({ mobile: e.target.value })} data-testid="in-mobile" /></div>
                  <div><L required>Email</L><I type="email" value={d.email} onChange={e => set({ email: e.target.value })} data-testid="in-email" /></div>
                  <div><L>Messenger / Facebook profile</L><I value={d.messenger} onChange={e => set({ messenger: e.target.value })} /></div>
                  <div><L>WhatsApp</L><I value={d.whatsapp} onChange={e => set({ whatsapp: e.target.value })} /></div>
                  <div><L>Viber</L><I value={d.viber} onChange={e => set({ viber: e.target.value })} /></div>
                  <div><L>Telegram</L><I value={d.telegram} onChange={e => set({ telegram: e.target.value })} /></div>
                  <div><L>Preferred contact method</L><I value={d.preferred_contact} onChange={e => set({ preferred_contact: e.target.value })} placeholder="Email / Phone / Messenger / WhatsApp / Viber / SMS" /></div>
                  <div><L>Emergency contact name</L><I value={d.emergency_contact_name} onChange={e => set({ emergency_contact_name: e.target.value })} /></div>
                  <div><L>Emergency contact number</L><I value={d.emergency_contact_number} onChange={e => set({ emergency_contact_number: e.target.value })} /></div>
                  <div><L>Relationship</L><I value={d.emergency_relationship} onChange={e => set({ emergency_relationship: e.target.value })} /></div>
                </Grid>
              </AccordionContent>
            </AccordionItem>

            {/* 03 IDs */}
            <AccordionItem value="p03">
              <AccordionTrigger data-testid="sec-03" className="font-semibold">03 · Identification / Employment Information</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <p className="text-xs text-slate-500">These details are never displayed publicly. Only authorized recruitment/admin access can see them.</p>
                <Grid>
                  <div><L>Government ID type</L><I value={d.gov_id_type} onChange={e => set({ gov_id_type: e.target.value })} /></div>
                  <div><L>Government ID number</L><I value={d.gov_id_number} onChange={e => set({ gov_id_number: e.target.value })} /></div>
                  <div><L>TIN</L><I value={d.tin} onChange={e => set({ tin: e.target.value })} /></div>
                  <div><L>SSS (optional)</L><I value={d.sss} onChange={e => set({ sss: e.target.value })} /></div>
                  <div><L>PhilHealth (optional)</L><I value={d.philhealth} onChange={e => set({ philhealth: e.target.value })} /></div>
                  <div><L>Pag-IBIG (optional)</L><I value={d.pagibig} onChange={e => set({ pagibig: e.target.value })} /></div>
                  <div><L>Driver's license number (if any)</L><I value={d.driver_license} onChange={e => set({ driver_license: e.target.value })} /></div>
                </Grid>
              </AccordionContent>
            </AccordionItem>

            {/* 04 Education & work */}
            <AccordionItem value="p04">
              <AccordionTrigger data-testid="sec-04" className="font-semibold">04 · Education & Work Experience</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <Grid>
                  <div><L>Highest education</L><I value={d.education_level} onChange={e => set({ education_level: e.target.value })} /></div>
                  <div><L>School / university</L><I value={d.school} onChange={e => set({ school: e.target.value })} /></div>
                  <div><L>Course / program</L><I value={d.course} onChange={e => set({ course: e.target.value })} /></div>
                  <div><L>Year completed</L><I value={d.year_completed} onChange={e => set({ year_completed: e.target.value })} /></div>
                  <div><L>Total work experience</L><I value={d.years_total_work} onChange={e => set({ years_total_work: e.target.value })} /></div>
                  <div><L>Years sales experience</L><I value={d.years_sales} onChange={e => set({ years_sales: e.target.value })} /></div>
                  <div><L>Years field-sales experience</L><I value={d.years_field_sales} onChange={e => set({ years_field_sales: e.target.value })} /></div>
                </Grid>
                <div><L>Previous job titles</L><I value={d.previous_titles} onChange={e => set({ previous_titles: e.target.value })} /></div>
                <div><L>Previous employers</L><I value={d.previous_employers} onChange={e => set({ previous_employers: e.target.value })} /></div>
                <div><L>Work experience description</L><T rows={4} value={d.work_description} onChange={e => set({ work_description: e.target.value })} /></div>
              </AccordionContent>
            </AccordionItem>

            {/* 05 Sales experience */}
            <AccordionItem value="p05">
              <AccordionTrigger data-testid="sec-05" className="font-semibold">05 · Sales Experience</AccordionTrigger>
              <AccordionContent>
                <CheckboxList options={SALES_EXPERIENCE_OPTIONS} value={d.sales_experience_types}
                                onChange={v => set({ sales_experience_types: v })} testPrefix="sales-type" />
              </AccordionContent>
            </AccordionItem>

            {/* 06 Previous performance */}
            <AccordionItem value="p06">
              <AccordionTrigger data-testid="sec-06" className="font-semibold">06 · Previous Sales Performance</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <Grid>
                  <div><L>What product/service did you sell?</L><I value={d.what_sold} onChange={e => set({ what_sold: e.target.value })} /></div>
                  <div><L>Who were the customers?</L><I value={d.customers} onChange={e => set({ customers: e.target.value })} /></div>
                  <div><L>B2B or B2C?</L><I value={d.b2b_or_b2c} onChange={e => set({ b2b_or_b2c: e.target.value })} /></div>
                  <div><L>Phone / online / face-to-face</L><I value={d.sales_channel} onChange={e => set({ sales_channel: e.target.value })} /></div>
                  <div><L>Prospects approached per day/week</L><I value={d.prospects_per_week} onChange={e => set({ prospects_per_week: e.target.value })} /></div>
                  <div><L>Typical sales closed</L><I value={d.typical_closes} onChange={e => set({ typical_closes: e.target.value })} /></div>
                  <div><L>Average monthly sales</L><I value={d.avg_monthly_sales} onChange={e => set({ avg_monthly_sales: e.target.value })} /></div>
                  <div><L>Largest sale / account</L><I value={d.largest_close} onChange={e => set({ largest_close: e.target.value })} /></div>
                  <div><L>Did you find your own leads?</L><I value={d.own_leads} onChange={e => set({ own_leads: e.target.value })} placeholder="Yes / No / Partly" /></div>
                  <div><L>Did you close yourself?</L><I value={d.closed_yourself} onChange={e => set({ closed_yourself: e.target.value })} placeholder="Yes / No / Partly" /></div>
                  <div><L>Did you have targets?</L><I value={d.had_targets} onChange={e => set({ had_targets: e.target.value })} placeholder="Yes / No" /></div>
                </Grid>
                <div><L>Did you normally meet targets? Explain.</L><T value={d.met_targets} onChange={e => set({ met_targets: e.target.value })} /></div>
                <div><L>Why did you leave your previous role?</L><T value={d.why_left} onChange={e => set({ why_left: e.target.value })} /></div>
              </AccordionContent>
            </AccordionItem>

            {/* 07 Skills */}
            <AccordionItem value="p07">
              <AccordionTrigger data-testid="sec-07" className="font-semibold">07 · Skill Self-Assessment (1–5)</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {SKILL_LIST.map(s => (
                    <div key={s} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1">
                      <span className="text-sm text-slate-700 flex-1">{s}</span>
                      <Rating value={d.skills[s]} onChange={v => setSkill(s, v)} testId={`skill-${s.slice(0,15)}`} />
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* 08 Languages */}
            <AccordionItem value="p08">
              <AccordionTrigger data-testid="sec-08" className="font-semibold">08 · Languages (1–5)</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {LANG_LIST.map(l => (
                    <div key={l} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1">
                      <span className="text-sm text-slate-700 flex-1">{l}</span>
                      <Rating value={d.languages[l]} onChange={v => setLang(l, v)} />
                    </div>
                  ))}
                </div>
                <div className="mt-4"><L>Other languages/dialects</L><I value={d.other_languages} onChange={e => set({ other_languages: e.target.value })} /></div>
              </AccordionContent>
            </AccordionItem>

            {/* 09 Field work & travel */}
            <AccordionItem value="p09">
              <AccordionTrigger data-testid="sec-09" className="font-semibold">09 · Field Work & Travel</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <Grid>
                  <div><L>Current location</L><I value={d.current_location} onChange={e => set({ current_location: e.target.value })} /></div>
                  <div><L>Primary area you can cover</L><I value={d.primary_area} onChange={e => set({ primary_area: e.target.value })} /></div>
                  <div><L>Other areas you can cover</L><I value={d.other_areas} onChange={e => set({ other_areas: e.target.value })} /></div>
                </Grid>
                <CheckboxList options={FIELD_CAPS} value={d.field_capabilities}
                                onChange={v => set({ field_capabilities: v })} testPrefix="field-cap" />
              </AccordionContent>
            </AccordionItem>

            {/* 10 Transportation */}
            <AccordionItem value="p10">
              <AccordionTrigger data-testid="sec-10" className="font-semibold">10 · Transportation</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <Grid>
                  <div><L>Primary transportation</L><I value={d.primary_transport} onChange={e => set({ primary_transport: e.target.value })} placeholder="Walking / Motorcycle / Car / Bus / Jeepney / …" /></div>
                  <div><L>Own transportation?</L><I value={d.owns_transport} onChange={e => set({ owns_transport: e.target.value })} placeholder="Yes / No" /></div>
                  <div><L>Vehicle registered / roadworthy?</L><I value={d.vehicle_registered} onChange={e => set({ vehicle_registered: e.target.value })} placeholder="Yes / No / Not applicable" /></div>
                  <div><L>Typical areas per day</L><I value={d.daily_coverage} onChange={e => set({ daily_coverage: e.target.value })} /></div>
                </Grid>
              </AccordionContent>
            </AccordionItem>

            {/* 11 Equipment */}
            <AccordionItem value="p11">
              <AccordionTrigger data-testid="sec-11" className="font-semibold">11 · Equipment</AccordionTrigger>
              <AccordionContent>
                <CheckboxList options={EQUIPMENT} value={d.equipment} onChange={v => set({ equipment: v })} testPrefix="equip" />
              </AccordionContent>
            </AccordionItem>

            {/* 12 Availability */}
            <AccordionItem value="p12">
              <AccordionTrigger data-testid="sec-12" className="font-semibold">12 · Availability</AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div>
                  <L>Work preference</L>
                  <CheckboxList options={WORK_PREF} value={d.work_preference} onChange={v => set({ work_preference: v })} />
                </div>
                <div>
                  <L>Available days</L>
                  <CheckboxList options={DAYS} value={d.available_days} onChange={v => set({ available_days: v })} testPrefix="day" />
                </div>
                <Grid>
                  <div><L>Available hours</L><I value={d.available_hours} onChange={e => set({ available_hours: e.target.value })} /></div>
                  <div><L>Earliest start date</L><I type="date" value={d.earliest_start} onChange={e => set({ earliest_start: e.target.value })} /></div>
                  <div><L>Expected monthly salary</L><I value={d.expected_monthly_salary} onChange={e => set({ expected_monthly_salary: e.target.value })} /></div>
                  <div><L>Expected daily field rate</L><I value={d.expected_daily_rate} onChange={e => set({ expected_daily_rate: e.target.value })} /></div>
                  <div><L>Expected commission / incentive</L><I value={d.expected_commission} onChange={e => set({ expected_commission: e.target.value })} /></div>
                  <div><L>OK with performance bonuses?</L><I value={d.ok_with_bonuses} onChange={e => set({ ok_with_bonuses: e.target.value })} placeholder="Yes / No" /></div>
                  <div><L>OK with measurable targets?</L><I value={d.ok_with_targets} onChange={e => set({ ok_with_targets: e.target.value })} placeholder="Yes / No" /></div>
                </Grid>
              </AccordionContent>
            </AccordionItem>

            {/* 13 Scenarios */}
            <AccordionItem value="p13">
              <AccordionTrigger data-testid="sec-13" className="font-semibold">13 · Practical Sales Questions</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {SCENARIOS.map(s => (
                  <div key={s.key}>
                    <L>{s.q}</L>
                    <T value={d.scenario_answers[s.key] || ""} onChange={e => setScen(s.key, e.target.value)} data-testid={`scenario-${s.key}`} />
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>

            {/* 14 Communication */}
            <AccordionItem value="p14">
              <AccordionTrigger data-testid="sec-14" className="font-semibold">14 · Communication Exercise</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <div><L>30-Second Pitch</L>
                  <p className="text-xs text-slate-500 mb-1">You walk into a small rental business. The owner gives you 30 seconds. Write exactly what you would say.</p>
                  <T rows={4} value={d.pitch_30s} onChange={e => set({ pitch_30s: e.target.value })} data-testid="pitch-30s" /></div>
                <div><L>One-sentence: What is ConfirmPH?</L>
                  <T rows={2} value={d.one_sentence} onChange={e => set({ one_sentence: e.target.value })} /></div>
                <div><L>Three discovery questions you would ask before demonstrating</L>
                  <div className="space-y-2">
                    <I value={d.discovery_q1} onChange={e => set({ discovery_q1: e.target.value })} placeholder="Question 1" />
                    <I value={d.discovery_q2} onChange={e => set({ discovery_q2: e.target.value })} placeholder="Question 2" />
                    <I value={d.discovery_q3} onChange={e => set({ discovery_q3: e.target.value })} placeholder="Question 3" />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* 15 Rejection */}
            <AccordionItem value="p15">
              <AccordionTrigger data-testid="sec-15" className="font-semibold">15 · Rejection & Persistence</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-700">Comfort with rejection</span>
                  <Rating value={d.rejection_comfort} onChange={v => set({ rejection_comfort: v })} />
                </div>
                <div><L>What do you do after rejection?</L><T value={d.after_rejection} onChange={e => set({ after_rejection: e.target.value })} /></div>
                <div><L>Businesses you're comfortable approaching in one day</L>
                  <I value={d.daily_approach_comfort} onChange={e => set({ daily_approach_comfort: e.target.value })} placeholder="1-5 / 6-10 / 11-15 / 16-20 / 20+" /></div>
                <div><L>Would repeated rejection affect you? Explain.</L><T value={d.rejection_impact} onChange={e => set({ rejection_impact: e.target.value })} /></div>
              </AccordionContent>
            </AccordionItem>

            {/* 16 Ethics */}
            <AccordionItem value="p16">
              <AccordionTrigger data-testid="sec-16" className="font-semibold">16 · Ethics Agreement (all required)</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {ETHICS_CLAUSES.map((c, i) => (
                    <label key={i} className="flex items-start gap-2 text-sm text-slate-700 py-1" data-testid={`ethic-${i}`}>
                      <input type="checkbox" checked={d.ethics[`e${i}`] === true}
                              onChange={e => setEth(`e${i}`, e.target.checked)}
                              className="mt-1 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* 17 Motivation */}
            <AccordionItem value="p17">
              <AccordionTrigger data-testid="sec-17" className="font-semibold">17 · Why ConfirmPH?</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {MOTIVATION_QS.map(([k, q]) => (
                  <div key={k}><L>{q}</L>
                    <T value={d.motivation[k] || ""} onChange={e => setMot(k, e.target.value)} /></div>
                ))}
              </AccordionContent>
            </AccordionItem>

            {/* 18 References */}
            <AccordionItem value="p18">
              <AccordionTrigger data-testid="sec-18" className="font-semibold">18 · References</AccordionTrigger>
              <AccordionContent className="space-y-4">
                {[0, 1].map(i => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3">
                    <div className="text-xs font-semibold text-slate-500 mb-2">Reference {i + 1}</div>
                    <Grid>
                      {[
                        ["name","Name"],["company","Company"],["position","Position"],
                        ["relationship","Relationship"],["phone","Phone"],["email","Email"],
                      ].map(([k,l]) => (
                        <div key={k}><L>{l}</L>
                          <I value={d.references[i]?.[k] || ""} onChange={e => setRef(i, { [k]: e.target.value })} /></div>
                      ))}
                    </Grid>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>

            {/* 19 Resume */}
            <AccordionItem value="p19">
              <AccordionTrigger data-testid="sec-19" className="font-semibold">19 · Resume / Supporting Documents</AccordionTrigger>
              <AccordionContent className="space-y-3">
                <p className="text-xs text-slate-500">File uploads coming soon. For now, paste a Google Drive / Dropbox / OneDrive link.</p>
                <div><L>Resume / CV link</L><I value={d.resume_url} onChange={e => set({ resume_url: e.target.value })} placeholder="https://drive.google.com/…" /></div>
                <div><L>LinkedIn URL</L><I value={d.linkedin_url} onChange={e => set({ linkedin_url: e.target.value })} /></div>
                <div><L>Portfolio / evidence link (optional)</L><I value={d.portfolio_url} onChange={e => set({ portfolio_url: e.target.value })} /></div>
              </AccordionContent>
            </AccordionItem>

            {/* 20 Privacy consent */}
            <AccordionItem value="p20" defaultChecked>
              <AccordionTrigger data-testid="sec-20" className="font-semibold">20 · Privacy Consent & Submission</AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-[#F8FAF9] p-4 text-sm text-slate-700 space-y-2">
                  <p>I understand that ConfirmPH may collect and process the personal information I submit for recruitment, applicant screening, communication, assessment, reference checking, possible contracting/employment, and related administrative purposes.</p>
                  <p>My application information is not intended to be displayed publicly and should only be accessible to authorized recruitment, management, and administrative personnel.</p>
                  <p>Submitting an application does not guarantee employment, assignment, interview, or compensation.</p>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="accept-accuracy">
                  <input type="checkbox" checked={d.accept_accuracy} onChange={e => set({ accept_accuracy: e.target.checked })}
                          className="mt-1 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  <span>I confirm that the information provided is accurate.</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="accept-privacy">
                  <input type="checkbox" checked={d.accept_privacy} onChange={e => set({ accept_privacy: e.target.checked })}
                          className="mt-1 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  <span>I agree to the applicant privacy and recruitment terms.</span>
                </label>

                <div className="pt-2">
                  <button onClick={submit} disabled={submitting || !requiredOK}
                          data-testid="submit-application"
                          className={`inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold ${
                            requiredOK && !submitting ? "bg-[#1B4332] text-white hover:bg-[#143027]"
                                                       : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}>
                    {submitting ? "Submitting…" : "Submit Application"} <Send className="w-4 h-4" />
                  </button>
                  {!requiredOK && (
                    <p className="mt-2 text-xs text-red-700">
                      Required to submit: full name, mobile, valid email, all ethics declarations, and both consent checkboxes.
                    </p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
}

function SubmittedScreen({ submitted }) {
  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-16" data-testid="submitted-screen">
      <div className="max-w-lg text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h1 className="mt-4 font-display text-3xl font-bold text-slate-900">Application Received</h1>
        <p className="mt-3 text-slate-600">Thank you for applying to become a ConfirmPH Sales Agent. Your application has been received successfully.</p>
        <div className="mt-4 inline-block rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700">
          Application reference: <span className="font-mono font-semibold text-slate-900" data-testid="submitted-ref">{submitted.reference}</span>
        </div>
        <p className="mt-4 text-sm text-slate-500">We may contact you if your application matches a current or future opportunity.</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-emerald-800 underline">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to ConfirmPH
        </Link>
      </div>
    </div>
  );
}
